import { z } from "zod";
// US-E6.F9-0412 (@UC0060): trạng thái của MỘT khoản phải đóng — TÁI DÙNG enum đã có ở collections,
// KHÔNG khai lại 7 giá trị lần thứ hai (drift whitelist là họ bệnh đắt nhất của repo này).
import { memberDueStatusSchema } from "./collections.js";

/**
 * Shared DTO cho Groups — POST /v1/groups (US-E4.F1-0018 / @UC0011).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN Phase 3: path canonical = `/v1/groups`. Slice 0018 = TỐI THIỂU:
 * chỉ tạo nhóm + creator auto Owner. Invitations/join/transfer/role-management → story E4 sau.
 *
 * Bảo mật (C2/C3): response KHÔNG mang field nhạy cảm — chỉ field cần cho UI điều hướng.
 */

/** Giới hạn độ dài tên nhóm (US-E4.F1-0018 / @UC0011 — invariant story ≤50 ký tự, AC3). */
export const GROUP_NAME_MAX_LENGTH = 50;

/** Thông điệp lỗi khi tên nhóm trống (AC2) — một nguồn sự thật api ↔ mobile. */
export const GROUP_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên nhóm";

/** Thông điệp lỗi khi tên nhóm vượt 50 ký tự (AC3). */
export const GROUP_NAME_TOO_LONG_MESSAGE = "Tên nhóm tối đa 50 ký tự";

/**
 * Body cho POST /v1/groups.
 * - name BẮT BUỘC: trim trước rồi validate 1..50 ký tự (đếm SAU trim).
 *   Rỗng/chỉ-khoảng-trắng → reject (AC2); >50 → reject (AC3).
 * - `.strict()` chặn field lạ tại biên (chống IDOR/over-post: KHÔNG nhận role/owner/id từ body —
 *   creator auto Owner do server quyết, client KHÔNG được tự gán).
 */
/**
 * US-E4.F1-0388-S01 (@UC0055) — category là DANH MỤC ĐỘNG (bảng `group_categories`), thay 6 nhãn
 * đóng của US-E23.F1-0331-S08.
 *
 * 🔴 QUAN HỆ MỚI giữa `category` và `type` (QĐ PO 12/08/2026, đảo QĐ cũ có ghi lý do):
 *  · `type` (`standard|parent`) vẫn là **LUẬT ánh xạ danh tính** — không đổi nghĩa.
 *  · Nhưng mỗi category trong bảng GẮN SẴN một `type` — server SUY `Group.type` từ category lúc
 *    tạo nhóm, client KHÔNG gửi `type` rời (hết ca cặp (type, category) mâu thuẫn nhau).
 *  · QĐ cũ "không suy cái này từ cái kia" tồn tại vì `other`/`hometown` mơ hồ — nay `other` tách
 *    thành `individual_person`/`family_couple`, mọi category đều có type xác định ⇒ lý do gốc hết.
 *
 * Contract chỉ còn gác **HÌNH DẠNG** code (snake_case) tại biên; tính hợp lệ NGỮ NGHĨA (code tồn
 * tại? đang `enabled`?) do FK + service quyết — closed-set sống trong DB, không trong file này.
 */
export const GROUP_CATEGORY_CODE_REGEX = /^[a-z][a-z0-9_]{1,49}$/;

export const groupCategorySchema = z
  .string()
  .regex(GROUP_CATEGORY_CODE_REGEX, "category phải là code snake_case (vd parent_class)");

export type GroupCategory = z.infer<typeof groupCategorySchema>;

/** Luật ánh xạ danh tính gắn cho category — đúng 2 giá trị, không nhận thứ ba. */
export const groupCategoryTypeSchema = z.enum(["standard", "parent"]);
export type GroupCategoryType = z.infer<typeof groupCategoryTypeSchema>;

/**
 * US-E4.F1-0431 (@UC9677) — TỪ VỰNG HIỂN THỊ theo loại nhóm.
 *
 * 24 khoá ARB của mobile đang ghim cứng "học sinh"/"phụ huynh" và hiện ở MỌI nhóm — hội đồng hương
 * cũng bị nhắc *"ghi chú cho phụ huynh về chi phí cho học sinh"*. Server cấp DANH TỪ, ARB giữ khung
 * câu và bơm danh từ vào.
 *
 * 🔴 KHOÁ THEO Ô NGHĨA, KHÔNG THEO MÀN. Sáu ô dưới đây phủ cả 24 nhãn; `seat_noun` một mình xuất
 * hiện ở 14 nhãn qua 3 màn. Khoá theo màn (`GROUP_DETAIL.*`, `ADD_MEMBER.*`) thì cùng một danh từ bị
 * CHÉP vào từng màn rồi trôi lệch — mẫu đề xuất ban đầu đã lệch ngay trong chính nó
 * ("Học sinh tên là" vs "Học sinh tên là..."). Thêm màn mới dùng lại `seat_noun` ⇒ 0 thay đổi ở đây.
 *
 * 🔴 `seat` / `representative`, KHÔNG `primary` / `dependent`. `primary_` đụng `isPrimaryContact`
 * và `primaryUserId` vốn nghĩa "đại diện CHÍNH" — khái niệm khác hẳn. Và phụ huynh không "phụ
 * thuộc" suất: SUẤT là thứ gánh nghĩa vụ tiền, phụ huynh ĐẠI DIỆN cho nó.
 *
 * 🔴 KHÔNG có cờ `has_dependent`: suy từ `type`. Hai nguồn cho một câu hỏi là mẫu bug lặp nhiều
 * nhất repo này.
 */
export const categoryLabelValueSchema = z
  .object({
    vi: z.string().min(1).max(120),
    en: z.string().min(1).max(120),
  })
  .strict();
export type CategoryLabelValue = z.infer<typeof categoryLabelValueSchema>;

export const categoryLabelsSchema = z
  .object({
    seat_noun: categoryLabelValueSchema.describe(
      "Danh từ chỉ NGƯỜI/THỰC THỂ mà SUẤT đại diện — thứ gánh nghĩa vụ tiền. Số ít, không viết hoa " +
        'toàn bộ, không mạo từ, không kèm ngữ cảnh. ĐÚNG: "Học sinh" · "Gia đình" · "Bé". ' +
        'SAI: "Các học sinh" (số nhiều) · "HS" (viết tắt) · "Học sinh lớp 1A" (kèm ngữ cảnh).',
    ),
    representative_noun: categoryLabelValueSchema
      .describe(
        "Danh từ chỉ NGƯỜI ĐẠI DIỆN cho suất. CHỈ dùng khi type=parent. KHÔNG hardcode 'Phụ huynh' " +
          'trong đầu: family_travel/family_couple thì người đại diện KHÔNG phải phụ huynh. ' +
          'ĐÚNG: "Phụ huynh" · "Người đại diện" · "Người giám hộ". SAI: "Bố mẹ học sinh" (kèm suất) · "PH".',
      )
      .optional(),
    seat_name_example: categoryLabelValueSchema
      .describe('Ví dụ TÊN của suất, hiện làm hint ô nhập. ĐÚNG: "Ví dụ: Nguyễn Văn A".')
      .optional(),
    representative_name_example: categoryLabelValueSchema
      .describe('Ví dụ TÊN người đại diện. ĐÚNG: "Ví dụ: Ba bé Long".')
      .optional(),
    birth_date_helper: categoryLabelValueSchema
      .describe("Câu giải thích vì sao ô ngày sinh bị khoá khi chọn suất CÓ SẴN.")
      .optional(),
    collection_desc_example: categoryLabelValueSchema
      .describe("Ví dụ mô tả đợt thu. 🔴 Bản ARB mặc định phải TRUNG TÍNH — ô này mới nêu chi phí đặc thù.")
      .optional(),
  })
  .strict();
export type CategoryLabels = z.infer<typeof categoryLabelsSchema>;

/**
 * US-E4.F1-0431 — cột `group_categories.labels` là JSONB ⇒ tầng đọc nhận `unknown`. Đây là CỬA
 * DUY NHẤT biến nó thành `CategoryLabels` cho response (group profile · members · invite preview ·
 * admin dictionary), để không ai `as CategoryLabels` thẳng tay.
 *
 * 🔴 FAIL-SOFT có chủ đích: dữ liệu lệch hình dạng (import tay, restore một phần) ⇒ trả `undefined`
 * ⇒ client rơi về ARB mặc định trung tính — ĐÚNG MỘT hành vi fallback như mọi ca thiếu pack khác
 * (task index §8). Ném 500 cả màn hình vì một cái nhãn trang trí là đổi lỗi hiển thị lấy lỗi chặn
 * đường; và admin vẫn sửa lại được bằng `PATCH /v1/admin/group-categories/:code` (@UC9678).
 * Hình dạng đã được gác hai lớp trước đó: zod ở biên ghi + CHECK `group_categories_labels_shape_check`.
 */
export function parseCategoryLabels(raw: unknown): CategoryLabels | undefined {
  if (raw === null || raw === undefined) return undefined;
  const parsed = categoryLabelsSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/**
 * US-E4.F1-0431-S03 — LUẬT `type` ↔ `labels` viết ĐÚNG MỘT LẦN.
 *
 * 🔴 Ba nơi cần cùng luật này: object `category` nhúng trong response (dưới đây), body
 * `POST /v1/admin/group-categories`, và service `PATCH /v1/admin/group-categories/:code` (nơi
 * `type` KHÔNG có trong body nên phải đối chiếu với row đang có ⇒ không dùng zod được).
 * Chép luật ra ba chỗ là mẫu "hai nguồn cho một câu hỏi" mà repo này đã trả giá nhiều lần: sửa
 * một chỗ quên hai chỗ ⇒ biên chặn còn service cho qua (hoặc ngược lại). Hàm THUẦN, dùng được cả
 * trong zod lẫn ngoài zod.
 *
 * Trả về câu giải thích khi vi phạm, `null` khi hợp lệ.
 */
export function categoryLabelsTypeViolation(
  type: GroupCategoryType,
  labels: CategoryLabels | null | undefined,
): string | null {
  if (!labels) return null;
  // 🔴 Trạng thái sai KHÔNG BIỂU DIỄN ĐƯỢC (CLAUDE.md §7): nhóm standard không có khái niệm
  // "người đại diện" nên gửi `representative_noun` là mô tả một thứ không tồn tại.
  if (type === "standard" && labels.representative_noun) {
    return "type=standard KHÔNG có người đại diện — bỏ representative_noun";
  }
  // Có `labels` cho nhóm parent thì phải đủ HAI danh từ nền: thiếu một cái là màn hiện nửa đúng
  // nửa mặc định, khó phát hiện hơn hẳn so với thiếu cả gói.
  if (type === "parent" && !labels.representative_noun) {
    return "type=parent có labels thì BẮT BUỘC representative_noun";
  }
  return null;
}

/** Đường dẫn issue chung cho vi phạm `type` ↔ `labels` — client trỏ đúng ô để sửa. */
export const CATEGORY_LABELS_ISSUE_PATH = ["labels", "representative_noun"] as const;

/** Bọc `categoryLabelsTypeViolation` thành refinement zod — dùng cho MỌI schema có cả type+labels. */
export function refineCategoryLabelsByType(
  value: { readonly type: GroupCategoryType; readonly labels?: CategoryLabels },
  ctx: z.RefinementCtx,
): void {
  const message = categoryLabelsTypeViolation(value.type, value.labels);
  if (message) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...CATEGORY_LABELS_ISSUE_PATH],
      message,
    });
  }
}

/**
 * Object `category` nhúng trong các response nhóm. Tách thành export CÓ TÊN (US-E4.F1-0431) để
 * ba response dùng CHUNG một hình dạng và để test đo được ràng buộc mà không phải dựng cả
 * `groupProfileResponse` — dựng thiếu field ở wrapper sẽ làm test âm XANH VÌ LÝ DO SAI.
 */
export const groupCategoryEmbeddedSchema = z
    .object({
    code: groupCategorySchema,
    /** Luật ánh xạ danh tính của CHÍNH nhóm này — dùng lại enum đã có, không khai chuỗi tự do. */
    type: groupCategoryTypeSchema,
    nameVi: z.string().min(1),
    nameEn: z.string().min(1),
    /**
     * US-E4.F1-0431 (@UC9677). VẮNG MẶT là hợp lệ và có nghĩa: client rơi về ARB mặc định
     * (trung tính). KHÔNG dùng `null` — "chưa cấu hình" và "cấu hình rỗng" phải cùng một hành vi,
     * hai cách biểu diễn cho một trạng thái là mời gọi hai nhánh xử lý.
     */
    labels: categoryLabelsSchema.optional(),
  })
    .strict()
    .superRefine(refineCategoryLabelsByType);
export type GroupCategoryEmbedded = z.infer<typeof groupCategoryEmbeddedSchema>;

/**
 * US-E4.F1-0431-S02 — MỘT cửa dựng object `category` cho CẢ BA response (`GET /groups/:id`,
 * `GET /groups/:id/members`, `POST /groups/:id/invitations/preview`).
 *
 * 🔴 Ba chỗ chứ không một, và đây là lý do (đừng gộp lại): *Chi tiết thành viên* của mobile dựng
 * dữ liệu từ `/groups/:id/members` chứ không có endpoint riêng; còn luồng MỜI chiếm 14/24 nhãn và
 * xảy ra TRƯỚC khi vào nhóm — người được mời chưa là member nên guard của `/groups/:id` trả 404.
 * Ba nơi dựng tay ⇒ ba hình dạng trôi lệch, nên chúng gọi CÙNG hàm này.
 *
 * 🔴 `groupType` là `groups.type` — LUẬT của CHÍNH NHÓM, KHÔNG phải `group_categories.type`
 * (US-E4.F1-0407 AC4b): nhãn là của danh mục, luật là của nhóm, và hai thứ đó lệch nhau được
 * trong dữ liệu thật. `members.service.ts` chặn tạo suất bằng cột này, nên client phải nhận đúng
 * cột này.
 *
 * 🔴 FAIL-SOFT ba nhánh, tất cả rơi về CÙNG một hành vi (ARB mặc định trung tính — task index §8):
 *   · không có danh mục ⇒ `null` (KHÔNG object rỗng: "chưa phân loại" phải phân biệt được với
 *     "có loại mà thiếu nhãn");
 *   · `groupType` ngoài whitelist (dữ liệu bẩn) ⇒ `null` — bịa `'standard'` sẽ khoá nhầm hành vi
 *     client ở một nhóm parent thật;
 *   · gói lệch hình dạng HOẶC không hợp với luật của nhóm ⇒ BỎ GÓI, giữ nhãn. Trả nguyên gói lệch
 *     sẽ tạo ra response vi phạm chính schema của nó ⇒ client `.strict()` vỡ CẢ MÀN vì một danh từ
 *     trang trí.
 */
export function buildGroupCategoryEmbedded(
  groupType: string,
  category:
    | { readonly code: string; readonly nameVi: string; readonly nameEn: string; readonly labels?: unknown }
    | null
    | undefined,
): GroupCategoryEmbedded | null {
  if (!category) return null;
  const parsedType = groupCategoryTypeSchema.safeParse(groupType);
  if (!parsedType.success) return null;
  const type = parsedType.data;

  const labels = parseCategoryLabels(category.labels);
  const usable = labels && !categoryLabelsTypeViolation(type, labels) ? labels : undefined;

  return {
    code: category.code,
    type,
    nameVi: category.nameVi,
    nameEn: category.nameEn,
    ...(usable ? { labels: usable } : {}),
  };
}


/**
 * Item catalog cho END-USER (`GET /v1/group-categories`): chỉ danh mục `enabled`, KHÔNG lộ
 * `status`/`id` — trạng thái quản trị không phải việc của client tạo nhóm. Server cấp nhãn song
 * ngữ vì danh mục động: client không thể ARB-map một code admin vừa tạo.
 */
export const publicGroupCategoryItemSchema = z
  .object({
    code: groupCategorySchema,
    type: groupCategoryTypeSchema,
    nameVi: z.string().min(1),
    nameEn: z.string().min(1),
  })
  .strict();
export type PublicGroupCategoryItem = z.infer<typeof publicGroupCategoryItemSchema>;

export const groupCategoriesResponseSchema = z
  .object({ data: z.array(publicGroupCategoryItemSchema) })
  .strict();
export type GroupCategoriesResponse = z.infer<typeof groupCategoriesResponseSchema>;

/** Mô tả nhóm (`Group.purpose`). Figma cho nhập mô tả khi tạo/sửa nhóm. */
export const GROUP_PURPOSE_MAX_LENGTH = 200;

export const createGroupRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, GROUP_NAME_REQUIRED_MESSAGE)
      .max(GROUP_NAME_MAX_LENGTH, GROUP_NAME_TOO_LONG_MESSAGE),
    /**
     * US-E4.F1-0388-S03 (QĐ PO 12/08/2026): `type` ĐÃ GỠ khỏi request — server SUY từ category
     * (bảng `group_categories` gắn sẵn type cho mỗi code). Client gửi `type` ⇒ 400 (.strict()).
     * Cùng logic chống-mâu-thuẫn của S08 cũ (không nhận mode rời) đẩy lên một nấc: không nhận
     * cả type rời — một nguồn quyết định duy nhất là category.
     * Vắng category ⇒ NULL + type `standard` (0 regress cho client chỉ gửi `{name}`).
     * Category paused/không tồn tại ⇒ 422 GROUP_CATEGORY_INVALID (service, fail-closed).
     */
    category: groupCategorySchema.optional(),
    purpose: z.string().trim().max(GROUP_PURPOSE_MAX_LENGTH).optional(),
  })
  .strict();

export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

/**
 * US-E23.F1-0331-S07 (@UC9512) — `PATCH /v1/groups/:id`.
 *
 * 🔴 CHỈ 3 field hồ sơ. KHÔNG có `defaultCurrency`: đổi đơn vị tiền của nhóm đã có sổ cái làm mọi
 * con số cũ mang nghĩa khác mà không bút toán nào giải thích (ledger ĐƠN-CURRENCY, ADR-0005).
 * KHÔNG có `type`: đổi luật ánh xạ danh tính giữa đời sống một nhóm sẽ mồ côi các MembershipAccount
 * đã tạo theo luật cũ.
 *
 * `.strict()` + `.refine` ít nhất 1 field: PATCH rỗng là request vô nghĩa, chặn ở biên thay vì để
 * service trả về "đã cập nhật" mà không cập nhật gì.
 */
export const patchGroupRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, GROUP_NAME_REQUIRED_MESSAGE)
      .max(GROUP_NAME_MAX_LENGTH, GROUP_NAME_TOO_LONG_MESSAGE)
      .optional(),
    /** `null` = xoá mô tả (tường minh), khác `undefined` = không đổi. */
    purpose: z.string().trim().max(GROUP_PURPOSE_MAX_LENGTH).nullable().optional(),
    avatarUrl: z.string().url().nullable().optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, {
    message: "Cần ít nhất một thông tin để cập nhật",
  });

export type PatchGroupRequest = z.infer<typeof patchGroupRequestSchema>;

/** Response `PATCH /groups/:id` + `POST /groups/:id/archive|unarchive`. */
export const groupProfileUpdateResponseSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    purpose: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  })
  .strict();

export type GroupProfileUpdateResponse = z.infer<typeof groupProfileUpdateResponseSchema>;

/**
 * Response `PUT /v1/groups/:id/avatar` (US-E24.F32-0446 · @UC9714) — upload ảnh đại diện NHÓM.
 *
 * 🔴 `avatarUrl` là **URL DÙNG ĐƯỢC NGAY** (public base nếu có, else presigned GET), KHÔNG phải
 * object key. Cột DB lưu key — bền vững; API trả URL — dùng được. Hai thứ khác nhau và client chỉ
 * bao giờ thấy thứ thứ hai.
 *
 * 🔴 `.url()` chứ không `.min(1)`: nếu một ngày server lỡ trả thẳng key (`group-avatars/…`) thì
 * `parse` NÉM ngay ở test contract, thay vì client âm thầm gán vào `<img src>` và hiện ảnh vỡ.
 * Đây là chốt chặn RẺ nhất cho đúng lỗi mà `group-avatar-resolve.invariants.spec.ts` sinh ra để chặn.
 * (`fundQrUploadResponseSchema` dùng `.min(1)` — ở đây cố ý chặt hơn, không phải quên đồng bộ.)
 */
export const groupAvatarUploadResponseSchema = z
  .object({
    avatarUrl: z.string().url(),
  })
  .strict();

export type GroupAvatarUploadResponse = z.infer<typeof groupAvatarUploadResponseSchema>;

export const groupArchiveResponseSchema = z
  .object({
    id: z.string().uuid(),
    /** ISO-8601 khi đã lưu trữ; `null` khi đang mở. */
    archivedAt: z.string().datetime().nullable(),
  })
  .strict();

export type GroupArchiveResponse = z.infer<typeof groupArchiveResponseSchema>;

/**
 * US-E24.F29-0432-S06 (`@UC0026`) — `DELETE /v1/groups/:id` (soft-delete).
 *
 * 🔴 SCHEMA RIÊNG, KHÔNG tái dùng `groupArchiveResponseSchema`. Hai hành động khác nhau ở đúng chỗ
 * quan trọng nhất — archive ĐẢO ĐƯỢC và giữ đường trả nợ sống; xoá thì không đảo được trong app và
 * khoá luôn đường trả nợ. Dùng chung một shape sẽ khiến client `switch` nhầm trường và báo sai cho
 * người dùng về một việc không hoàn tác được.
 */
export const groupSoftDeleteResponseSchema = z
  .object({
    id: z.string().uuid(),
    /** ISO-8601 thời điểm xoá. Không bao giờ `null` ở response thành công — nhưng giữ nullable cho
     * khớp cột DB, chốt shape là việc của `.strict()` chứ không phải của một phép `as`. */
    deletedAt: z.string().datetime().nullable(),
  })
  .strict();

export type GroupSoftDeleteResponse = z.infer<typeof groupSoftDeleteResponseSchema>;

/**
 * US-E23.F1-0331-S06 (@UC9511) — code 409 khi WRITE nghiệp vụ trên nhóm ĐÃ LƯU TRỮ.
 *
 * 🔴 CỐ Ý KHÁC `GROUP_SUSPENDED_CODE`: đình chỉ là admin hệ thống cưỡng chế (tạm thời), lưu trữ là
 * chính nhóm tự đóng sổ (quyết định cuối vòng đời). Gộp hai mã lại buộc client nói một câu mơ hồ
 * *"nhóm bị khoá"* trong khi cách xử lý của người dùng hoàn toàn khác nhau: đình chỉ thì liên hệ hỗ
 * trợ, lưu trữ thì owner tự mở lại được.
 *
 * READ **không** bị chặn — xem lại toàn bộ sổ của nhóm đã đóng chính là điểm của "lưu trữ thay xoá".
 * Đường TRẢ NỢ cũ cũng không bị chặn (freeze chỉ gắn ở `/funds/:id/**` — nơi sinh nghĩa vụ MỚI).
 */
export const GROUP_ARCHIVED_CODE = "GROUP_ARCHIVED" as const;
export const GROUP_ARCHIVED_MESSAGE =
  "Nhóm đã lưu trữ — không tạo được đợt thu hay khoản chi mới. Mở lại nhóm nếu cần tiếp tục.";

/**
 * Role của member trong nhóm (Phase 3 RBAC). Một nguồn sự thật cho api + mobile + task E4 sau.
 * M5: tránh magic-string ('owner'/'admin'/...) rải rác — dùng hằng GROUP_ROLE.* / GroupRole type.
 */
export const GROUP_ROLE = {
  OWNER: "owner",
  ADMIN: "admin",
  TREASURER: "treasurer",
  MEMBER: "member",
} as const;

export const groupRoleSchema = z.enum([
  GROUP_ROLE.OWNER,
  GROUP_ROLE.ADMIN,
  GROUP_ROLE.TREASURER,
  GROUP_ROLE.MEMBER,
]);
export type GroupRole = z.infer<typeof groupRoleSchema>;

/**
 * Trạng thái membership (Phase 3). Một nguồn sự thật cho api + task E4 sau (M5).
 * Khớp schema.prisma GroupMember.status: active | invited | join_pending | removed | left | unclaimed.
 *
 * 🔴🔴 US-E24.F3-0352 (@UC9573) — `unclaimed` VÀO ĐÂY, thôi khai tay.
 *
 * TRƯỚC ĐÓ: `unclaimed` (US-IDM — "chỗ ngồi" chưa có người đại diện: học sinh admin import, hoặc suất
 * phụ huynh vừa tạo) tồn tại THẬT trong DB và trong `schema.prisma:508`, nhưng **KHÔNG** có ở enum này
 * ⇒ được khai bằng **magic string ở 2 file** (`identity-selection.service.ts`,
 * `member-account-mapping.service.ts`), mỗi file một bản. Đúng tiền lệ *"drift 2 whitelist"* của repo
 * (§3.8): hai danh sách rời nhau thì sớm muộn lệch nhau, và lệch ở đây nghĩa là **một suất có nghĩa vụ
 * tiền lọt khỏi mọi bộ lọc** — bug #5 sống được chính vì không danh sách nào biết `unclaimed` là gì.
 *
 * 🔴 Nới enum ở đây là **nới cái ĐƯỢC PHÉP BIỂU DIỄN**, KHÔNG nới cái được LỌC MẶC ĐỊNH: các đường đọc
 * (`GET /groups/:id/members`, `listMyGroups`, `countActiveMembers`) vẫn chốt `ACTIVE` như cũ. Xem
 * `memberListStatusFilterSchema` — muốn thấy `unclaimed` thì phải HỎI TƯỜNG MINH.
 */
export const GROUP_MEMBER_STATUS = {
  ACTIVE: "active",
  INVITED: "invited",
  JOIN_PENDING: "join_pending",
  REMOVED: "removed",
  LEFT: "left",
  /** "Chỗ ngồi" chưa có người đại diện (US-IDM ghost import / suất phụ huynh vừa tạo). */
  UNCLAIMED: "unclaimed",
} as const;

export const groupMemberStatusSchema = z.enum([
  GROUP_MEMBER_STATUS.ACTIVE,
  GROUP_MEMBER_STATUS.INVITED,
  GROUP_MEMBER_STATUS.JOIN_PENDING,
  GROUP_MEMBER_STATUS.REMOVED,
  GROUP_MEMBER_STATUS.LEFT,
  GROUP_MEMBER_STATUS.UNCLAIMED,
]);
export type GroupMemberStatus = z.infer<typeof groupMemberStatusSchema>;

/**
 * US-E24.F3-0352-S03 (@UC9574) — tập trạng thái LỌC ĐƯỢC ở `GET /v1/groups/:id/members`.
 *
 * 🔴 **Vắng tham số ⇒ giữ nguyên `active`.** Đổi mặc định thành "trả cả unclaimed" là **thay đổi phá
 * vỡ** cho mọi client đang đếm sĩ số (thủ quỹ đếm đầu người ra một con số khác sau một lần deploy).
 * Suất kẹt phải NHÌN THẤY ĐƯỢC — nhưng bằng cách HỎI, không bằng cách đổi câu trả lời mặc định.
 *
 * Cố ý **KHÔNG** mở `removed`/`left`/`invited`/`join_pending` ở đây: chúng không phải "suất đang gánh
 * nghĩa vụ tiền", và mở rộng bộ lọc là mở rộng bề mặt rò danh sách người đã rời nhóm.
 */
/**
 * 🔴 US-E4.F1-0411 (QĐ người dùng 14/08/2026) — KHÔNG phải một trạng thái mà là một **TẬP**:
 * mọi SUẤT ĐANG SỐNG (`active` + `unclaimed`), tức đã có hoặc chưa có người đại diện.
 *
 * Vì sao là một giá trị riêng chứ không bắt client gửi `?status=active,unclaimed`: "suất đang sống
 * gồm những trạng thái nào" là một LUẬT NGHIỆP VỤ. Client tự liệt kê thì ngày enum nới thêm một
 * trạng thái, mọi client đều mục — đúng hai lần repo này đã dính (`unclaimed` thiếu ở enum client,
 * `join_pending` thiếu ở `toFilterValue`). Server giữ luật, client chỉ hỏi.
 *
 * Loại `join_pending`/`left`/`removed`: đó là người ĐÃ RỜI hoặc BỊ GỠ. Danh sách thành viên là
 * danh sách người đang gánh nghĩa vụ, không phải lịch sử ai từng ở đây.
 */
export const MEMBER_LIST_STATUS_SEATS = "seats" as const;

export const memberListStatusFilterSchema = z.enum([
  GROUP_MEMBER_STATUS.ACTIVE,
  GROUP_MEMBER_STATUS.UNCLAIMED,
  MEMBER_LIST_STATUS_SEATS,
  /**
   * 🔴 US-E4.F2-0396 (QĐ PO 13/08/2026) — HÀNG ĐỢI: người đã ở trong nhóm nhưng CHƯA được công nhận
   * là thành viên (vừa bị gỡ, hoặc tự rời rồi muốn quay lại). Vô hình với tiền/quorum/quyền xem.
   *
   * Mở đúng MỘT giá trị này, và có lý do cụ thể: không nhìn thấy hàng đợi thì O/A/T không add ai
   * vào lại được — danh sách tồn tại để được xử lý. Doc ngay trên vẫn đúng cho
   * `removed`/`left`/`invited`: chúng là quá khứ, không phải việc đang chờ làm.
   *
   * 🔴 Kèm điều kiện BẮT BUỘC ở tầng service: chỉ O/A/T đọc được giá trị này (member thường ⇒ 403).
   * Hàng đợi là danh sách QUẢN TRỊ; để member thường liệt kê được là phơi ra ai vừa bị gỡ khỏi nhóm.
   */
  GROUP_MEMBER_STATUS.JOIN_PENDING,
]);
export type MemberListStatusFilter = z.infer<typeof memberListStatusFilterSchema>;

/**
 * US-E4.F2-0396-S02 (@UC0027) — `POST /v1/groups/:id/members`: admin tạo SUẤT ĐẠI DIỆN hàng loạt.
 *
 * "Suất đại diện" = `GroupMember` KHÔNG gắn user nào (`primaryUserId` null, `status=unclaimed`) —
 * vd học sinh trong nhóm phụ huynh. Phụ huynh gắn vào sau, qua `/members/:memberId/accounts` hoặc
 * One-Time Identity Selection.
 *
 * 🔴 CHỈ nhóm `parent`. Nhóm `standard` là 1-1 (một user = một suất) nên "suất không có người" ở đó
 * là một chỗ ngồi vĩnh viễn trống — sinh ra nó chỉ tạo rác và làm sĩ số nói dối.
 */
export const createGroupMemberSeatSchema = z
  .object({
    /** Tên hiển thị của suất (tên học sinh). BẮT BUỘC: suất không tên thì admin không phân biệt nổi. */
    displayName: z.string().trim().min(1).max(120),
    /** Ngày sinh học sinh — tuỳ chọn, `YYYY-MM-DD` (ngày trần, KHÔNG mốc thời gian). */
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();

export const createGroupMemberSeatsRequestSchema = z
  .object({
    // Trần 100/lần: nhập cả lớp là ~50, nhân đôi cho thoải mái. Không mở vô hạn — một request
    // nghìn dòng là một transaction dài khoá bảng member của cả nhóm.
    members: z.array(createGroupMemberSeatSchema).min(1).max(100),
  })
  .strict();
export type CreateGroupMemberSeatsRequest = z.infer<typeof createGroupMemberSeatsRequestSchema>;

export const createGroupMemberSeatsResponseSchema = z
  .object({
    data: z.array(
      z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        status: z.literal(GROUP_MEMBER_STATUS.UNCLAIMED),
      }),
    ),
  })
  .strict();
export type CreateGroupMemberSeatsResponse = z.infer<typeof createGroupMemberSeatsResponseSchema>;

/**
 * US-E4.F2-0396-S03 (@UC0027) — `POST /v1/groups/:id/members/add`: công nhận người ở HÀNG ĐỢI
 * thành thành viên thật.
 *
 * Hai nhánh loại trừ nhau (`.strict()` + refine ⇒ gửi cả hai hoặc không gửi gì đều 400):
 *  · `userIds`     — nhóm **standard** 1-1: mỗi user nhận suất của chính mình (`linkRole=self`).
 *  · `assignments` — nhóm **parent**: gắn user vào một suất đại diện CÓ SẴN (`linkRole` mặc định
 *    `primary_contact` — người liên hệ chính của học sinh đó).
 *
 * 🔴 Chỉ add được người ĐANG Ở HÀNG ĐỢI của chính nhóm này. Không có đường "add thẳng user bất kỳ":
 * vào nhóm phải qua lời mời hoặc yêu cầu tham gia — nếu không, một admin có thể kéo người lạ vào
 * nhóm rồi gán nghĩa vụ tiền cho họ.
 */
export const addGroupMembersRequestSchema = z
  .object({
    userIds: z.array(z.string().uuid()).min(1).max(100).optional(),
    assignments: z
      .array(
        z
          .object({
            userId: z.string().uuid(),
            groupMemberId: z.string().uuid(),
            linkRole: z.enum(["primary_contact", "secondary_contact"]).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(100)
      .optional(),
  })
  .strict()
  .refine((v) => Boolean(v.userIds) !== Boolean(v.assignments), {
    message: "Gửi ĐÚNG MỘT trong hai: `userIds` (nhóm standard) hoặc `assignments` (nhóm parent).",
  });
export type AddGroupMembersRequest = z.infer<typeof addGroupMembersRequestSchema>;

export const addGroupMembersResponseSchema = z
  .object({
    /** Số suất chuyển sang `active`. Client dùng để hiện toast "Đã thêm N thành viên". */
    added: z.number().int().min(0),
  })
  .strict();
export type AddGroupMembersResponse = z.infer<typeof addGroupMembersResponseSchema>;

/**
 * Shape response cho POST /v1/groups.
 * Trả group mới + role của caller (owner) để mobile điều hướng Group Profile (story 0020).
 * KHÔNG lộ danh sách member/secret — chỉ field tối thiểu cho điều hướng (C2/C3).
 */
export const groupResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  // Role của người gọi trong nhóm vừa tạo — luôn "owner" cho slice 0018.
  role: groupRoleSchema,
  createdAt: z.string().datetime(),
});

export type GroupResponse = z.infer<typeof groupResponseSchema>;

/* ===========================================================================
 * GET /v1/users/me/groups — View My Group List (US-E4.F2-0019 / @UC0015).
 *
 * Cross-check API_BUILD_PLAN Phase 3: endpoint list-my-groups KHÔNG có sẵn trong danh sách
 * Phase 3 (plan chỉ POST/PATCH /groups, members/role, transfer, invitations/join). Đây là read
 * list hợp lý thuộc scope Groups → bổ sung khi build Phase 3 (xem index §6 + SD-2).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII (C2): chỉ nhóm caller là member ACTIVE (server lọc theo token, chống IDOR) — item KHÔNG
 *    mang field cross-user/secret. `.strict()` chặn field lạ lọt ra response.
 *  - ⚠️ KHÔNG có field SỐ DƯ/tiền: số dư quỹ phụ thuộc Ledger Phase 6 (chưa build) → CẮT phạm vi,
 *    KHÔNG bịa giá trị tài chính (guardrail A4/B1). Tách story khi Ledger xong.
 *  - Cursor opaque string — đối xứng common/pagination/cursor (envelope { data, pageInfo }).
 * ========================================================================= */

/** Max page-size cho list my-groups. Default (20) do backend áp khi vắng. */
export const MY_GROUPS_MAX_LIMIT = 50;

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS). */
export const MY_GROUPS_MAX_QUERY_LENGTH = 512;

/**
 * Một item trong danh sách "Nhóm của tôi" (AC1, KHÔNG số dư):
 *  - id/name: định danh + tên nhóm (tap → Group Profile, AC3).
 *  - avatarUrl: ảnh nhóm; null khi nhóm chưa đặt avatar.
 *  - memberCount: số thành viên ACTIVE của nhóm (≥1, vì caller cũng là member active).
 *  - role: vai trò của CHÍNH caller trong nhóm (owner|admin|treasurer|member) — cho role badge.
 *  - status: trạng thái membership của caller — luôn 'active' ở list này (server đã lọc), giữ field
 *    cho status badge (AC1) + tường minh contract.
 *  - needsAttention: cờ "việc cần xử lý" tối giản (AC2 — warning badge). Nội dung THẬT phụ thuộc
 *    Collections P7 / Voting P8 (chưa build) → hiện luôn false (KHÔNG bịa logic nghiệp vụ).
 * `.strict()` chặn rò field thừa (vd balance/userId/secret) ra response.
 */
/**
 * 🔴 US-E23.F1-0331-S01 (@UC9510) — placeholder khi ẩn tiền ở TẦNG SERVER.
 *
 * §3.8: *"Redaction ẩn ở TẦNG SERVER = `'•••'`, KHÔNG `0`"* — `0` là **nói dối về tiền**: quỹ có 5
 * triệu mà hiện `0` thì thành viên tưởng quỹ rỗng và mất niềm tin, đúng thứ sản phẩm này tồn tại để
 * xây. Số bị ẩn **không rời API**.
 *
 * Mirror `PUBLIC_STATEMENT_REDACTED` (compliance.ts) — CÙNG placeholder, KHÔNG đẻ hằng thứ hai.
 */
export const GROUP_MONEY_REDACTED = "•••" as const;

/** Tiền trên wire = STRING minor (JSON không có int64 an toàn). Không float, không number. */
const GROUP_MONEY_INTEGER_STRING = z.string().regex(/^-?(0|[1-9][0-9]*)$/);

/** Field không-tiền của một item "Nhóm của tôi" — phần dùng chung cho cả hai nhánh redaction. */
const myGroupListItemBase = {
  id: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  memberCount: z.number().int().min(0),
  role: groupRoleSchema,
  status: groupMemberStatusSchema,
  needsAttention: z.boolean(),
  /**
   * US-E23.F1-0331 (@UC9511): Figma hiện trạng thái *"Lưu trữ"* ở 4 chỗ mà repo có **0 endpoint**.
   * Đây là vế đọc; vế ghi ở S06 (`/archive`, `/unarchive`) — **thay cho xoá cứng**, vì §3.8
   * *"GDPR = ẨN DANH, KHÔNG XOÁ SỔ"* và 9 bảng có trigger DB cấm DELETE.
   */
  isArchived: z.boolean(),
};

/**
 * 🔴 DISCRIMINATED UNION theo `redacted`, KHÔNG phải field optional.
 *
 * Nhánh ẩn mang literal `'•••'` ⇒ **trạng thái sai không biểu diễn được**: không thể vừa
 * `redacted: true` vừa mang số thật, và không thể lỡ trả `0` khi ý là "ẩn". Nếu dùng
 * `balanceMinor?: string` thì cả hai lỗi đó đều **hợp lệ về kiểu** và chỉ lộ ra ở mắt người dùng.
 */
export const myGroupListItemSchema = z.discriminatedUnion("redacted", [
  z.object({ ...myGroupListItemBase, redacted: z.literal(false), balanceMinor: GROUP_MONEY_INTEGER_STRING, currency: z.string().length(3) }).strict(),
  z.object({ ...myGroupListItemBase, redacted: z.literal(true), balanceMinor: z.literal(GROUP_MONEY_REDACTED), currency: z.string().length(3) }).strict(),
]);

export type MyGroupListItem = z.infer<typeof myGroupListItemSchema>;

/** Query list: cursor opaque optional, limit ∈ [1,50] optional (default 20 backend áp). */
export const myGroupsListQuerySchema = z.object({
  cursor: z.string().max(MY_GROUPS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(MY_GROUPS_MAX_LIMIT).optional(),
});

export type MyGroupsListQuery = z.infer<typeof myGroupsListQuerySchema>;

/**
 * Envelope list khớp quy ước repo `{ data, pageInfo }`. KHÔNG có field tổng số dư (Phase 6 deferred).
 * data sắp xếp activity gần nhất lên đầu (group.updatedAt desc — AC5).
 */
export const myGroupsListResponseSchema = z.object({
  data: z.array(myGroupListItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type MyGroupsListResponse = z.infer<typeof myGroupsListResponseSchema>;

/* ===========================================================================
 * GET /v1/groups/:id — View Group Profile (US-E4.F3-0020 / @UC0055).
 *
 * Cross-check API_BUILD_PLAN Phase 3: `GET /v1/groups/:id` là read-detail bổ trợ cho Phase 3
 * Groups (plan liệt kê POST/PATCH /groups + members/role; read-detail là complement hợp lý,
 * thêm theo SD-2 giống list-my-groups). Member-only qua GroupRoleGuard (tenant-isolation 404
 * đồng nhất cho non-member/cross-tenant — chống IDOR, đã chốt ở 0018).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII / tenant isolation (C2/C4): chỉ member ACTIVE của nhóm xem được (server enforce qua guard).
 *  - ⚠️ KHÔNG có field SỐ DƯ/tiền quỹ: số dư phụ thuộc Ledger Phase 6 (chưa build) — CẮT phạm vi,
 *    KHÔNG bịa giá trị tài chính (guardrail A4/B1). Fund item chỉ id/name + cờ needsConfig.
 *  - Role của caller trả về để client gate action (AC2) — backend KHÔNG dựng action button.
 *  - `.strict()` chặn rò field thừa (vd balance/bankAccount/secret) ra response.
 * ========================================================================= */

/**
 * Một quỹ trong Group Profile (AC1/AC4 — KHÔNG số dư):
 *  - id/name: định danh + tên quỹ (tap → Fund deep-screen thuộc story 0021).
 *  - needsConfig: cờ "chưa cấu hình tài khoản ngân hàng" (Fund.bankAccount null) — client render
 *    badge "Chưa cấu hình" (AC4). KHÔNG lộ nội dung bankAccount (chỉ cờ boolean).
 * `.strict()` chặn rò field tài chính/secret (balance/bankAccount) ra response.
 */
export const groupProfileFundItemSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    needsConfig: z.boolean(),
  })
  .strict();

export type GroupProfileFundItem = z.infer<typeof groupProfileFundItemSchema>;

/**
 * Cờ action role-gated (AC2) — canManageMembers/canEditSettings/canCreateFund = true CHỈ khi
 * role owner/admin. Server tính (single source of truth), client KHÔNG tự nâng quyền (C4).
 *
 * 🔴 US-E24.F31-0436 — HAI MỨC QUYỀN trên cùng một màn, đừng gộp:
 *  - `canManageMembers` = owner **HOẶC** admin. Khớp `DELETE :id/members/:uid` (owner+admin).
 *  - `canChangeRole` / `canTransferOwnership` = **CHỈ owner**. Khớp
 *    `@RequireGroupRoles(GROUP_ROLE.OWNER)` ở `groups.controller.ts` cho
 *    `POST :id/members/:mid/role` và `POST :id/ownership/transfer`.
 *
 * Trước 17/08/2026 chỉ có cờ mức trên, nên màn hình muốn ẩn hai nút owner-only phải **tự suy** từ
 * `role` — đúng thứ docblock của kiểu này cấm. Thiếu cờ không gây lỗi tiền lẫn lỗi quyền (server
 * vẫn 403), nhưng nó dẫn quản trị viên vào một nút chắc chắn hỏng.
 */
export const groupProfileActionsSchema = z
  .object({
    canManageMembers: z.boolean(),
    canEditSettings: z.boolean(),
    canCreateFund: z.boolean(),
    /** CHỈ owner — xem docblock trên. */
    canChangeRole: z.boolean(),
    /** CHỈ owner. Hành động **không đảo được**, không có bước người kia chấp nhận. */
    canTransferOwnership: z.boolean(),
  })
  .strict();

export type GroupProfileActions = z.infer<typeof groupProfileActionsSchema>;

/**
 * Shape response cho GET /v1/groups/:id (group shell + role-gated flags + KHỐI TÀI CHÍNH):
 *  - id/name/purpose(mô tả)/avatarUrl: header nhóm. purpose/avatarUrl null khi chưa đặt.
 *  - memberCount: số GroupMember ACTIVE của nhóm (≥1, vì caller cũng là member active).
 *  - role: vai trò của CHÍNH caller (owner|admin|treasurer|member) — client gate action (AC2).
 *  - funds: danh sách quỹ chưa archive (id/name/needsConfig) — KHÔNG số dư từng quỹ (ngoài phạm vi).
 *  - actions: cờ role-gated (AC2). Server tính từ role; client KHÔNG tự nâng quyền.
 *
 * US-E4.F3-0389 (@UC0055): thêm khối tài chính cấp group (comment cũ "KHÔNG số dư — Phase 6
 * deferred" đã lỗi thời từ khi `sumGroupCashBalances` tồn tại):
 *  - `balanceMinor`: Σ CASH các quỹ active.
 *  - `totalIncomeMinor`: tổng THU lũy kế (Σ account INCOME, ĐÃ đảo dấu về dương — cache lưu
 *    debit-dương nên INCOME credit-normal nằm âm trong DB).
 *  - `totalExpenseMinor`: tổng CHI lũy kế (Σ account EXPENSE, debit-normal — giữ nguyên).
 *  - DISCRIMINATED UNION theo `redacted` (mirror `myGroupListItemSchema`): nhánh ẩn mang literal
 *    '•••' cho CẢ BA số — trạng thái "vừa ẩn vừa mang số thật" không biểu diễn được. Luật: O/A/T
 *    luôn thấy; member thường cần MỌI quỹ `transparency_mode=public` (tổng là số cộng gộp — ẩn
 *    một phần trong con số cộng gộp là không ẩn được).
 * `.strict()` chặn rò field thừa (bankAccount/secret) ra response (C2).
 */
const groupProfileBase = {
  id: z.string().uuid(),
  name: z.string(),
  purpose: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  memberCount: z.number().int().min(0),
  role: groupRoleSchema,
  funds: z.array(groupProfileFundItemSchema),
  actions: groupProfileActionsSchema,
  currency: z.string().length(3),
  /**
   * US-E4.F1-0395 (@UC0055) — loại nhóm dạng OBJECT kèm NHÃN SONG NGỮ. `null` khi chưa phân loại.
   *
   * 🔴 ĐẢO quyết định 0388 ("trả CODE, nhãn lấy từ `GET /v1/group-categories`"), có lý do:
   * bắt client tải cả danh mục rồi TỰ TRA mã→nhãn là bắt nó dựng lại một sự thật server đã biết —
   * FK `categoryRef` nằm ngay trong query hồ sơ, `select` qua quan hệ là CÙNG một round-trip.
   * Tệ hơn: hai request phải cùng thành công để vẽ đúng MỘT cái nhãn, nên danh mục lỗi ⇒ chip biến
   * mất dù nhóm có đặt loại hẳn hoi ⇒ người dùng thấy nhóm mình "không có loại".
   * Lý do cũ ("một nguồn nhãn duy nhất") vẫn được giữ: nguồn vẫn là bảng `group_categories`, chỉ
   * khác là SERVER tra hộ thay vì client tra.
   *
   * 🔴 CÓ `type` ở đây — **ĐẢO quyết định cũ, QĐ người dùng 14/08/2026.**
   *
   * Câu cũ ở chỗ này là *"KHÔNG có `type`, cố ý — lộ ra biên end-user là mời viết
   * `if (type == 'parent')` ở client"*. Nỗi lo đó đúng về nguyên tắc nhưng đã bị một ràng buộc
   * THẬT vượt qua: `POST /v1/groups/:id/members` (tạo suất đại diện) ném **422
   * `GROUP_SEATS_PARENT_ONLY`** cho nhóm `standard` (`members.service.ts` đọc `groups.type`). Client
   * phải ẩn HẲN lối đó ở nhóm standard — để người dùng gõ xong cả danh sách học sinh rồi mới ăn 422
   * là thiết kế tồi hơn nhiều so với một nhánh `if` ở client.
   *
   * Vì sao KHÔNG tra qua `GET /v1/group-categories` cho đỡ đổi contract: `parent` không phải MỘT
   * code mà là **ba** (`parent_class`, `family_couple`, `family_travel`), và danh mục **mở** — admin
   * thêm được lúc chạy qua `POST /admin/dictionaries/group-categories`. Danh sách code chép tay ở
   * client sẽ mục đúng như enum `GroupCategory` 6-giá-trị cũ đã mục (4 code mới rơi vào `null` im
   * lặng). Còn tra qua catalog thì bắt màn chi tiết nhóm gọi thêm một request nữa — chính là thứ
   * đoạn doc ngay trên vừa bác bỏ khi đảo QĐ 0388.
   *
   * ⇒ Server tra hộ, một round-trip, một nguồn sự thật (`group_categories.type`).
   *
   * `GET /v1/group-categories` GIỮ NGUYÊN: màn *tạo nhóm* cần liệt kê loại để người dùng CHỌN —
   * bài toán khác với *hiển thị* loại của một nhóm đã có.
   */
  category: groupCategoryEmbeddedSchema.nullable(),
};

export const groupProfileResponseSchema = z.discriminatedUnion("redacted", [
  z
    .object({
      ...groupProfileBase,
      redacted: z.literal(false),
      balanceMinor: GROUP_MONEY_INTEGER_STRING,
      totalIncomeMinor: GROUP_MONEY_INTEGER_STRING,
      totalExpenseMinor: GROUP_MONEY_INTEGER_STRING,
    })
    .strict(),
  z
    .object({
      ...groupProfileBase,
      redacted: z.literal(true),
      balanceMinor: z.literal(GROUP_MONEY_REDACTED),
      totalIncomeMinor: z.literal(GROUP_MONEY_REDACTED),
      totalExpenseMinor: z.literal(GROUP_MONEY_REDACTED),
    })
    .strict(),
]);

export type GroupProfileResponse = z.infer<typeof groupProfileResponseSchema>;

/**
 * Role được phép thao tác quản lý (quản lý thành viên / cài đặt nhóm / tạo quỹ) trong nhóm.
 * Một nguồn sự thật cho api (tính actions) + test. AC2: CHỈ owner/admin (treasurer/member view-only).
 */
export const GROUP_MANAGE_ROLES: readonly GroupRole[] = [
  GROUP_ROLE.OWNER,
  GROUP_ROLE.ADMIN,
];

/**
 * Tính cờ action role-gated từ role caller (AC2).
 *
 * owner/admin → nhóm cờ "quản lý" true. **CHỈ owner** → hai cờ owner-only true.
 *
 * 🔴 `isOwner` cố ý so sánh TRỰC TIẾP với `GROUP_ROLE.OWNER`, không dùng một hằng danh sách như
 * `GROUP_MANAGE_ROLES`: một danh sách tên chung sẽ có ngày bị ai đó thêm `ADMIN` vào cho tiện, và
 * lúc đó hai cờ này lặng lẽ nói sai về quyền. Ở đây chỉ có đúng một vai, viết thẳng ra.
 */
export function computeGroupProfileActions(
  role: GroupRole,
): GroupProfileActions {
  const canManage = GROUP_MANAGE_ROLES.includes(role);
  const isOwner = role === GROUP_ROLE.OWNER;
  return {
    canManageMembers: canManage,
    canEditSettings: canManage,
    canCreateFund: canManage,
    canChangeRole: isOwner,
    canTransferOwnership: isOwner,
  };
}

/* ===========================================================================
 * GET /v1/groups/:id/members — View Member List (US-E4.F4-0022 / @UC0055).
 *
 * Cross-check API_BUILD_PLAN Phase 3: `GET /v1/groups/:id/members` thuộc Groups Phase 3
 * (members.service trên module groups). Member-only qua GroupRoleGuard (tenant-isolation 404
 * đồng nhất cho non-member/cross-tenant/:id-không-UUID — chống IDOR, đã chốt ở 0018).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII / tenant isolation (C2/C4): chỉ member ACTIVE của nhóm xem danh sách (server enforce qua
 *    guard). CHỈ trả thành viên status='active' (loại invited/join_pending/removed/left — AC3).
 *  - ⚠️ KHÔNG đụng tài chính/tiền (story xem danh sách thuần). KHÔNG lộ email/phone thô (chỉ
 *    fullName/displayName/avatar cho UI). `.strict()` chặn rò field thừa ra response.
 *  - actions role-gated (canInviteMembers/canManageMembers) tính từ role caller — owner/admin true,
 *    treasurer/member false (AC1/AC2). Server là single source of truth; client KHÔNG tự nâng quyền.
 *    KHÔNG remove/invite thật ở đây (story 0023/0027) — chỉ visibility flags.
 * ========================================================================= */

/** Max page-size cho list members. Default (20) do backend áp khi vắng. */
export const GROUP_MEMBERS_MAX_LIMIT = 50;

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS). */
export const GROUP_MEMBERS_MAX_QUERY_LENGTH = 512;

/**
 * Một item trong Member List (AC1 — KHÔNG tài chính):
 *  - id: GroupMember.id (định danh membership — dùng cho remove/role action story sau).
 *  - userId: User.id (chủ membership). KHÔNG lộ email/phone.
 *  - fullName: tên thật của user; null khi chưa đặt.
 *  - displayName: nickname per-group (GroupMember.displayName, E2-25); null khi không đặt.
 *  - avatarUrl: ảnh đại diện user; null khi chưa có.
 *  - role: vai trò trong nhóm (owner|admin|treasurer|member) — cho role-badge.
 *  - joinedAt: thời điểm tham gia (ISO-8601); null nếu thiếu (dữ liệu cũ).
 * `.strict()` chặn rò field thừa (email/phone/passwordHash/secret) ra response (C2).
 */
const memberListItemBase = {
  id: z.string().uuid(),
  // 🔴 US-E24.F3-0352-S03 (@UC9574): NULLABLE. Suất `unclaimed` (chỗ ngồi chưa có người đại diện)
  // KHÔNG có `primaryUserId` — trước đây schema đòi uuid nên chỉ cần lọt một suất như vậy vào response
  // là `.strict()` nổ 500. Nới NULL là điều kiện cần để `?status=unclaimed` biểu diễn được sự thật
  // "suất này chưa có ai đứng tên"; `0`/chuỗi rỗng đều là bịa.
  userId: z.string().uuid().nullable(),
  fullName: z.string().nullable(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: groupRoleSchema,
  // 🔴 US-E24.F3-0352-S03 (@UC9574): CÓ cờ phân biệt `active` vs `unclaimed` ngay trong item. Không có
  // nó thì client lọc `?status=unclaimed` xong vẫn không dựng được một danh sách trộn, và "suất chưa
  // có chủ" lại vô hình lần nữa — lần này ở tầng UI.
  status: memberListStatusFilterSchema,
  joinedAt: z.string().datetime().nullable(),
};

/**
 * US-E23.F1-0331-S01/S04 (@UC9510) — thêm `totalContributedMinor` (Figma hiện tổng đã đóng của từng
 * người trong danh sách thành viên; contract cũ không có trường đó).
 *
 * 🔴 CÙNG luật redaction với `myGroupListItemSchema`: union theo `redacted`, nhánh ẩn = `'•••'`.
 * 🔴 `.strict()` VẪN chặn email/phone — bài học US-E4.F4-0022 (list member có test quét JSON chống
 * lộ PII). Thêm tiền vào response KHÔNG được nới cái chặn đó.
 */
export const memberListItemSchema = z.discriminatedUnion("redacted", [
  z.object({ ...memberListItemBase, redacted: z.literal(false), totalContributedMinor: GROUP_MONEY_INTEGER_STRING }).strict(),
  z.object({ ...memberListItemBase, redacted: z.literal(true), totalContributedMinor: z.literal(GROUP_MONEY_REDACTED) }).strict(),
]);

export type MemberListItem = z.infer<typeof memberListItemSchema>;

/**
 * Cờ action role-gated cho Member List (AC1/AC2) — canInviteMembers/canManageMembers = true CHỈ khi
 * role owner/admin. Server tính (single source of truth), client KHÔNG tự nâng quyền (C4).
 * canInviteMembers điều khiển nút "+" (route mời 0023); canManageMembers điều khiển mgmt action (0027).
 */
export const memberListActionsSchema = z
  .object({
    canInviteMembers: z.boolean(),
    canManageMembers: z.boolean(),
  })
  .strict();

export type MemberListActions = z.infer<typeof memberListActionsSchema>;

/** Query list members: cursor opaque optional, limit ∈ [1,50] optional, filter role optional. */
export const memberListQuerySchema = z.object({
  cursor: z.string().max(GROUP_MEMBERS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(GROUP_MEMBERS_MAX_LIMIT).optional(),
  /** Lọc theo role (owner|admin|treasurer|member). Vắng ⇒ tất cả role. Validate biên ở enum. */
  role: groupRoleSchema.optional(),
  /**
   * US-E24.F3-0352-S03 (@UC9574) — lọc theo trạng thái suất. **Vắng ⇒ `active`** (0 regress).
   * `?status=unclaimed` ⇒ chỉ suất chưa có người đại diện. Giá trị ngoài enum ⇒ 400 ở biên.
   */
  status: memberListStatusFilterSchema.optional(),
  /**
   * US-E4.F4-0387 (@UC0055) — tìm theo TÊN: khớp một-phần, case-insensitive, trên `fullName`
   * HOẶC `displayName` (hai field đã public sẵn trong response — KHÔNG mở search theo email/phone,
   * PII đó cố ý không có mặt ở bề mặt này). AND với role/status/cursor. Vắng ⇒ không lọc (0 regress).
   */
  search: z.string().trim().min(1).max(100).optional(),
});

export type MemberListQuery = z.infer<typeof memberListQuerySchema>;

/**
 * Envelope list members khớp quy ước repo `{ data, pageInfo }` + role/actions của caller.
 *  - data: danh sách member active (sort ổn định để cursor đáng tin).
 *  - role: vai trò của CHÍNH caller — client gate action (AC1/AC2).
 *  - actions: cờ role-gated của caller (nút "+"/mgmt). Server tính; client KHÔNG tự nâng quyền.
 * `.strict()` chặn rò field thừa ra response (C2).
 */
export const memberListResponseSchema = z
  .object({
    data: z.array(memberListItemSchema),
    role: groupRoleSchema,
    actions: memberListActionsSchema,
    /**
     * US-E4.F1-0431-S02 (@UC9677) — LOẠI NHÓM + gói từ vựng, đặt ở CẤP RESPONSE (không phải trong
     * từng item: nó là thuộc tính của NHÓM, lặp lại ở mỗi dòng là mời gọi lệch).
     *
     * 🔴 Vì sao endpoint NÀY chứ không chỉ hồ sơ nhóm: màn *Chi tiết thành viên* của mobile dựng
     * dữ liệu từ đây (`getGroupMembers(seatsOnly: true)`), nó KHÔNG có endpoint riêng. Đặt gói ở
     * `GET /groups/:id` thì màn đó không lấy được và phải gọi thêm một request chỉ để lấy danh từ.
     *
     * BẮT BUỘC có mặt (nullable, không optional): nhóm chưa gán loại ⇒ `null`. Cho phép vắng mặt
     * nghĩa là một nhánh code quên điền sẽ đi qua lặng lẽ.
     */
    category: groupCategoryEmbeddedSchema.nullable(),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
      /**
       * US-E4.F4-0387 (@UC0055) — số item CÒN LẠI sau trang này, TÔN TRỌNG mọi filter đang áp
       * (search/role/status). FE render nút "Xem thêm min(remainingCount, limit)" — trang áp chót
       * lộ đúng số lẻ (vd "+5" thay vì "+10"). `hasNext=false ⇔ remainingCount=0`.
       * Cơ chế global: `PageInfo` chung (`common/pagination/cursor.ts`) khai field này OPTIONAL —
       * list khác adopt dần không vỡ shape; response NÀY bắt buộc vì đã wire.
       */
      remainingCount: z.number().int().min(0),
    }),
  })
  .strict();

export type MemberListResponse = z.infer<typeof memberListResponseSchema>;

/** Tính cờ action Member List từ role caller (AC1/AC2). owner/admin → mọi cờ true; còn lại false. */
export function computeMemberListActions(role: GroupRole): MemberListActions {
  const canManage = GROUP_MANAGE_ROLES.includes(role);
  return {
    canInviteMembers: canManage,
    canManageMembers: canManage,
  };
}

/* ===========================================================================
 * PATCH /v1/groups/:id/membership — TÊN CỦA TÔI TRONG NHÓM NÀY (US-E2.F1-0399 · @UC0055).
 *
 * Đặt tên theo `:id/membership` cho khớp `DELETE :id/membership` (rời nhóm) đã có — cùng một nghĩa
 * "quan hệ của CHÍNH TÔI với nhóm này". KHÔNG đụng `User.fullName` toàn cục: đổi tên trong quỹ lớp
 * không được đổi tên ở quỹ công ty.
 *
 * 🔴 BẤT BIẾN SỐ MỘT — KHÔNG BAO GIỜ ghi tên lên `group_members.display_name` của suất có
 * `linkRole ≠ self`. Suất đó là MỘT ĐỨA TRẺ: phụ huynh gõ "Ba bé Long" mà ghi đè lên suất thì tên
 * đứa trẻ hoá tên bố, và cái tên sai đó xuất hiện trên mọi đợt thu + sổ nợ của lớp.
 *
 * ⇒ Server rẽ nhánh theo `linkRole` **ĐÃ LƯU** của account người gọi (dữ liệu), KHÔNG suy từ người
 * bấm nút hay từ body:
 *   · `self`                              → `group_members.display_name`      → target `"member"`
 *   · `primary_contact`/`secondary_contact` → `membership_accounts.display_name` → target `"account"`
 *   · `viewer`                            → 403 (người quan sát không mang danh tính trong nhóm)
 *   · không có account nào                → 404 (đồng nhất với cross-tenant)
 * ========================================================================= */

/**
 * Trần tên hiển thị trong nhóm = **30**, BẰNG `FULL_NAME_MAX_LENGTH` (`users-me.ts`) — cùng loại dữ
 * liệu (tên người) thì cùng trần. Lệch trần là chỗ này cho qua chỗ kia chặn.
 * Khoá bằng test `group-display-name.test.ts` để hai hằng không trôi khỏi nhau trong im lặng.
 * 🔴 DB cố ý KHÔNG có CHECK độ dài (migration 20260813120000) ⇒ biên HTTP này là chốt chặn DUY NHẤT.
 */
export const GROUP_DISPLAY_NAME_MAX_LENGTH = 30;

/** Thông điệp lỗi — một nguồn sự thật api ↔ mobile (client vẫn map theo `code`, không theo câu chữ). */
export const GROUP_DISPLAY_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên hiển thị";
export const GROUP_DISPLAY_NAME_TOO_LONG_MESSAGE = "Tên hiển thị tối đa 30 ký tự";

/**
 * `target` — nhánh ghi mà server ĐÃ CHỌN. Phơi ra CÓ CHỦ ĐÍCH: nó biến "ghi vào suất hay vào
 * account" thành thứ QUAN SÁT ĐƯỢC từ test và từ client, thay vì hành vi ngầm chỉ soi DB mới biết.
 *  - `member`  : đã ghi tên của SUẤT   (`group_members.display_name`).
 *  - `account` : đã ghi tên của NGƯỜI  (`membership_accounts.display_name`) — tên suất KHÔNG đổi.
 */
export const GROUP_DISPLAY_NAME_TARGET = {
  MEMBER: "member",
  ACCOUNT: "account",
} as const;

export type GroupDisplayNameTarget =
  (typeof GROUP_DISPLAY_NAME_TARGET)[keyof typeof GROUP_DISPLAY_NAME_TARGET];

export const groupDisplayNameTargetSchema = z.enum([
  GROUP_DISPLAY_NAME_TARGET.MEMBER,
  GROUP_DISPLAY_NAME_TARGET.ACCOUNT,
]);

/**
 * Body `PATCH /v1/groups/:id/membership`.
 *  - `displayName` BẮT BUỘC: trim trước rồi đếm 1..30 (rỗng/chỉ-khoảng-trắng ⇒ 400; >30 ⇒ 400).
 *    Không có nhánh "vắng field = giữ nguyên": PATCH rỗng là no-op câm, người dùng tưởng đã đổi.
 *  - `.strict()`: KHÔNG nhận `userId`/`target` từ client — server tự quyết ghi vào ĐÂU theo
 *    `linkRole` đã lưu. Nhận `target` từ body chính là mở đường ghi đè tên đứa trẻ.
 *
 * 🔴 `memberId` TUỲ CHỌN, và nó KHÔNG phải "cho client chọn nhánh ghi" — nó trả lời câu hỏi KHÁC:
 * **đổi tên của tôi Ở SUẤT NÀO.**
 *
 * Một user có thể có NHIỀU suất trong cùng một nhóm: `membership_accounts` chỉ unique theo
 * `(groupMemberId, userId)`, không giới hạn số suất — sinh đôi là ca thật, bố mẹ gắn vào cả hai
 * đứa. (Chỉ `primaryUserId` mới bị chặn 1-suất-1-nhóm bởi partial unique
 * `one_active_seat_per_user_per_group`, mà `identity-selection` cố ý KHÔNG set cột đó.)
 *
 * Vắng `memberId` khi người gọi có ĐÚNG MỘT suất ⇒ dùng suất đó. Có ≥2 suất ⇒ **409 fail-closed**,
 * KHÔNG chọn bừa: chọn nhầm nghĩa là đặt tên lên quan hệ với đứa con KHÁC. Đây đúng nguyên tắc
 * "0 hoặc ≥2 khớp thì phải hỏi lại, không đoán" mà repo đã ghi cho lớp AI — cùng một loại nguy hiểm.
 */
export const updateMyGroupDisplayNameRequestSchema = z
  .object({
    displayName: z
      .string()
      .trim()
      .min(1, GROUP_DISPLAY_NAME_REQUIRED_MESSAGE)
      .max(GROUP_DISPLAY_NAME_MAX_LENGTH, GROUP_DISPLAY_NAME_TOO_LONG_MESSAGE),
    memberId: z.string().uuid().optional(),
  })
  .strict();

export type UpdateMyGroupDisplayNameRequest = z.infer<
  typeof updateMyGroupDisplayNameRequestSchema
>;

/** Response 200: tên đã lưu (sau trim) + nhánh ghi. `.strict()` chặn rò field thừa. */
export const updateMyGroupDisplayNameResponseSchema = z
  .object({
    displayName: z.string(),
    target: groupDisplayNameTargetSchema,
  })
  .strict();

export type UpdateMyGroupDisplayNameResponse = z.infer<
  typeof updateMyGroupDisplayNameResponseSchema
>;

/** Mã lỗi máy-đọc-được (RFC7807 `code`) — client map code→message, KHÔNG so khớp `detail`. */
export const GROUP_DISPLAY_NAME_CODE = {
  /** `linkRole='viewer'`: quan sát viên không mang danh tính trong nhóm ⇒ 403. */
  VIEWER_NO_IDENTITY: "GROUP_DISPLAY_NAME_VIEWER_NO_IDENTITY",
  /**
   * Người gọi có ≥2 suất trong nhóm mà body không nói suất nào ⇒ **409, KHÔNG chọn bừa**.
   *
   * Ca thật: bố mẹ của một cặp sinh đôi, gắn vào cả hai suất. "Tên của tôi trong nhóm này" lúc đó
   * là hai câu trả lời khác nhau ("Mẹ bé An" / "Mẹ bé Bình"), và máy không có cơ sở nào để chọn.
   */
  SEAT_AMBIGUOUS: "GROUP_DISPLAY_NAME_SEAT_AMBIGUOUS",
} as const;

export const GROUP_DISPLAY_NAME_VIEWER_MESSAGE =
  "Bạn đang xem nhóm với vai người quan sát nên chưa có tên hiển thị để đổi.";

export const GROUP_DISPLAY_NAME_SEAT_AMBIGUOUS_MESSAGE =
  "Bạn đang đại diện cho nhiều người trong nhóm này. Hãy chọn bạn muốn đổi tên ở người nào.";

/* ===========================================================================
 * GET /v1/groups/:id/members/:memberId/contributions — CHI TIẾT ĐÓNG GÓP CỦA MỘT SUẤT
 * (US-E6.F9-0412 · @UC0060 @UC0055 @UC0058).
 *
 * VÌ SAO CÓ: màn "Chi tiết thành viên" ở mobile đang hiện SỐ TIỀN BỊA trên một màn nói về một người
 * CÓ THẬT. Trước task này backend chỉ trả lời được *"đợt thu X ai đã đóng"*
 * (`/collections/:id/members-status`) và *"CHÍNH TÔI trong quỹ Y"* (`/funds/:id/dues/me`) — không có
 * đường nào trả lời *"người này đã đóng bao nhiêu, còn thiếu bao nhiêu, xuyên suốt nhóm"*.
 *
 * 🔴 HAI TRỤC ĐỘC LẬP (CLAUDE.md §7 P5.3 — *"nhóm có thể minh bạch tiền mà vẫn giấu tên, hoặc ngược
 * lại"*). Đừng gộp:
 *  - **DANH TÍNH** (ai được mở hồ sơ của ai) — luật của `MemberDueReadAccessGuard`, tái dùng nguyên
 *    văn: chủ SUẤT xem của mình · O/A/T xem của người khác · còn lại **404 đồng nhất**. Trục này KHÔNG
 *    biểu diễn được bằng redaction: không cho xem thì không trả về gì cả.
 *  - **TIỀN** (thấy SỐ hay `'•••'`) — `transparency_mode`, CÙNG luật với `totalContributedMinor` ở
 *    `GET /v1/groups/:id/members`. Đó là lý do hai nhánh dưới đây tồn tại.
 * ========================================================================= */

/** Max page-size cho lịch sử đóng góp. Default (20) do `normalizeLimit` backend áp khi vắng. */
export const MEMBER_CONTRIBUTIONS_MAX_LIMIT = 50;

/**
 * 🔴 US-E24.F51-0503 (@UC0060) — CAP PHÒNG THỦ cho `outstanding[]`, **không phải phân trang**.
 *
 * `outstanding[]` trả TRỌN có chủ đích (AC8): nó trả lời *"tôi còn phải trả những gì"*, và câu trả
 * lời đó không được phụ thuộc người dùng đã cuộn lịch sử tới đâu. Số khoản còn thiếu của MỘT người
 * là hữu hạn nhỏ — 200 kỳ chưa đóng là bệnh lý dữ liệu, không phải cách dùng bình thường.
 *
 * Cap tồn tại để một suất bệnh lý không đẻ ra response khổng lồ. Hai điều làm nó KHÔNG nói dối:
 *  1. `totalOwedMinor` luôn là tổng của **TOÀN BỘ** dues (nguồn `FinancialPositionService`), không
 *     phải tổng của mảng này ⇒ con số "còn thiếu bao nhiêu" vẫn đúng kể cả khi mảng bị cắt.
 *  2. Mảng sắp theo **hạn gần nhất trước** ⇒ thứ bị cắt là thứ ÍT cấp bách nhất.
 */
export const MEMBER_CONTRIBUTIONS_OUTSTANDING_CAP = 200;

/** Độ dài tối đa cursor opaque tại biên (chặn input phình to — DoS), mirror member list. */
export const MEMBER_CONTRIBUTIONS_MAX_QUERY_LENGTH = 512;

/**
 * Field KHÔNG-TIỀN của một khoản trong lịch sử — phần dùng chung cho cả hai nhánh redaction.
 *
 * 🔴 CỐ Ý KHÔNG mang field danh tính (`memberName`/`avatarUrl`/`groupMemberId`): đây là lịch sử của
 * ĐÚNG MỘT người, danh tính nằm một lần ở cấp response. Nhét tên vào từng dòng sẽ biến response này
 * thành "danh sách người kèm tiền" — thứ mà `person-payment-exposure.invariants.spec.ts` (@UC9582)
 * canh, và đúng ra phải canh.
 */
const memberContributionEntryBase = {
  memberDueId: z.string().uuid(),
  collectionId: z.string().uuid(),
  /** Tên đợt thu; `null` khi đợt thu chưa đặt tiêu đề (KHÔNG bịa chuỗi thay thế ở server). */
  collectionTitle: z.string().nullable(),
  /**
   * Trạng thái THẬT của khoản (passthrough từ `member_dues`, KHÔNG recompute). Có nó thì client phân
   * biệt được "đóng đủ" với "đóng dở 150k/500k" — không có thì hai chuyện đó trông y hệt nhau.
   */
  status: memberDueStatusSchema,
  /** Mốc ghi nhận đã đóng; `null` với dữ liệu cũ chưa có cột — không bịa ngày. */
  paidAt: z.string().datetime().nullable(),
  /** Khoá sort/keyset của cursor (`createdAt` luôn có, `paidAt` thì không). */
  createdAt: z.string().datetime(),
};

/** Một khoản ĐÃ VÀO THẬT, có SỐ (người xem được phép thấy tiền). */
export const memberContributionEntrySchema = z
  .object({
    ...memberContributionEntryBase,
    /**
     * 🔴 Số tiền THỰC SỰ đã vào cho khoản này (`collectedMinorOf` — nguồn chung @UC9659), KHÔNG phải
     * số PHẢI đóng: khoản đóng dở 150k/500k hiện `150000`, không phải `500000`.
     */
    amountMinor: GROUP_MONEY_INTEGER_STRING,
  })
  .strict();

/** Cùng khoản đó khi người xem KHÔNG được phép thấy tiền — `'•••'`, KHÔNG `0`. */
export const redactedMemberContributionEntrySchema = z
  .object({
    ...memberContributionEntryBase,
    amountMinor: z.literal(GROUP_MONEY_REDACTED),
  })
  .strict();

/* ---------------------------------------------------------------------------
 * US-E24.F51-0503 (@UC0060 @UC0055 @UC9659) — KHOẢN **CÒN THIẾU**.
 *
 * VÌ SAO PHẢI CÓ DANH SÁCH, KHÔNG CHỈ `totalOwedMinor`: tổng nói *"còn thiếu 1.050.000đ"* nhưng
 * không nói **thiếu ở đợt nào, hạn bao giờ**. Người dùng biết mình nợ mà không biết đi trả cái gì —
 * và một app về NIỀM TIN thì không được để người ta ở trạng thái đó.
 *
 * 🔴 MỘT KHOẢN CÓ THỂ NẰM Ở CẢ `data[]` LẪN `outstanding[]`, VỚI HAI CON SỐ KHÁC NHAU — và đó là
 * ĐÚNG, không phải trùng lặp. Khoản đóng dở 150k/500k: `data[]` mang `150000` (tiền ĐÃ vào —
 * `collectedMinorOf`), `outstanding[]` mang `350000` (phần CÒN thiếu — `outstandingMinorOf`). Hai
 * danh sách trả lời hai câu hỏi khác nhau về cùng một khoản tiền. Bất biến khoá bằng test:
 * `collectedMinorOf + outstandingMinorOf = amountMinor` cho mọi status trừ `waived`.
 *
 * ⚠️ `waived` KHÔNG thuộc bên nào (và không vào `totalOwedMinor`): miễn thì không kỳ vọng thu. Đưa nó
 * vào `outstanding[]` là đòi tiền một khoản đã tha.
 *
 * ⚠️ `awaiting_confirmation` thuộc **CÒN THIẾU** (§3.7 — ba trạng thái tiền không được gộp): người
 * bấm *"tôi đã chuyển"* mới ở trạng thái (2) KHAI BÁO, chưa phải (3) XÁC NHẬN NHẬN. Máy không bao
 * giờ tự in "đã nhận".
 * ------------------------------------------------------------------------- */

/**
 * Field KHÔNG-TIỀN của một khoản còn thiếu — dùng chung cho cả hai nhánh redaction, để hai nhánh
 * không thể trôi khỏi nhau.
 *
 * 🔴 `status` cố ý dùng **NGUYÊN** `memberDueStatusSchema` chứ không phải một enum con "chỉ các
 * trạng thái còn thiếu". Whitelist chọn khoản nào là còn thiếu sống ở MỘT chỗ duy nhất
 * (`OUTSTANDING_DUE_STATUSES` — `collection-progress.math.ts`); chép một bản thứ hai xuống contract
 * là dựng đúng cái *"drift 2 whitelist"* mà §3.8 cấm.
 */
const memberContributionOutstandingBase = {
  memberDueId: z.string().uuid(),
  collectionId: z.string().uuid(),
  /** Tên đợt thu; `null` khi đợt chưa đặt tiêu đề (KHÔNG bịa chuỗi thay thế ở server). */
  collectionTitle: z.string().nullable(),
  status: memberDueStatusSchema,
  /**
   * Hạn chót của đợt. `null` khi đợt **không đặt hạn** — KHÔNG bịa một ngày ra. Client hiển thị
   * "chưa có hạn"; một ngày bịa sẽ đẻ ra cảnh báo trễ hạn cho khoản chưa bao giờ có hạn.
   */
  dueDate: z.string().datetime().nullable(),
};

/** Một khoản CÒN THIẾU, có SỐ (người xem được phép thấy tiền). */
export const memberContributionOutstandingSchema = z
  .object({
    ...memberContributionOutstandingBase,
    /**
     * 🔴 Phần CÒN THIẾU (`outstandingMinorOf` — nguồn chung @UC9659), KHÔNG phải số phải đóng:
     * khoản đóng dở 150k/500k hiện `350000`, không phải `500000`.
     */
    amountMinor: GROUP_MONEY_INTEGER_STRING,
  })
  .strict();

/** Cùng khoản đó khi người xem KHÔNG được phép thấy tiền — `'•••'`, KHÔNG `0`. */
export const redactedMemberContributionOutstandingSchema = z
  .object({
    ...memberContributionOutstandingBase,
    amountMinor: z.literal(GROUP_MONEY_REDACTED),
  })
  .strict();

/** Field không-tiền ở cấp response. */
const memberContributionsBase = {
  /** SUẤT được hỏi (echo lại để client chắc chắn đang render đúng người). */
  groupMemberId: z.string().uuid(),
  currency: z.string().length(3),
  pageInfo: z
    .object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    })
    .strict(),
};

/**
 * 🔴 DISCRIMINATED UNION theo `redacted`, KHÔNG phải field optional (mirror `memberListItemSchema` /
 * `myGroupListItemSchema`). Kiểu này làm cho *"vừa ẩn vừa mang số thật"* và *"ẩn nhưng trả `0`"*
 * **không biểu diễn được** — bug không phải bị bắt lúc chạy mà là không viết ra nổi.
 *
 * Cả tổng LẪN từng dòng đều đổi nhánh cùng lúc: ẩn tổng mà để lộ từng dòng thì cộng tay là ra.
 * 🔴 US-E24.F51-0503: `outstanding[]` đi CÙNG nhánh với `data[]` và hai tổng — vì đúng lý lẽ đó, chỉ
 * ở chiều ngược lại (ẩn "đã đóng" mà phơi "còn thiếu" thì trừ ra là biết).
 */
export const memberContributionsResponseSchema = z.discriminatedUnion("redacted", [
  z
    .object({
      ...memberContributionsBase,
      redacted: z.literal(false),
      /** Σ tiền ĐÃ XÁC NHẬN NHẬN (§3.7 trạng thái 3). Claim "tôi đã chuyển" KHÔNG được cộng vào. */
      totalPaidMinor: GROUP_MONEY_INTEGER_STRING,
      /** Σ còn thiếu — nguồn `FinancialPositionService`, KHÔNG `account_balances`. */
      totalOwedMinor: GROUP_MONEY_INTEGER_STRING,
      data: z.array(memberContributionEntrySchema),
      /**
       * Các khoản CÒN THIẾU — **KHÔNG phân trang theo cursor của `data[]`** (AC8), trả trọn kèm cap.
       * Field **BẮT BUỘC**, không optional: vắng mặt sẽ được client đọc thành *"không còn thiếu gì"*,
       * tức một bản build thiếu sót có thể nói dối về tiền bằng cách im lặng.
       */
      outstanding: z
        .array(memberContributionOutstandingSchema)
        .max(MEMBER_CONTRIBUTIONS_OUTSTANDING_CAP),
    })
    .strict(),
  z
    .object({
      ...memberContributionsBase,
      redacted: z.literal(true),
      totalPaidMinor: z.literal(GROUP_MONEY_REDACTED),
      totalOwedMinor: z.literal(GROUP_MONEY_REDACTED),
      data: z.array(redactedMemberContributionEntrySchema),
      outstanding: z
        .array(redactedMemberContributionOutstandingSchema)
        .max(MEMBER_CONTRIBUTIONS_OUTSTANDING_CAP),
    })
    .strict(),
]);

export type MemberContributionEntry = z.infer<typeof memberContributionEntrySchema>;
export type MemberContributionOutstanding = z.infer<typeof memberContributionOutstandingSchema>;
export type MemberContributionsResponse = z.infer<typeof memberContributionsResponseSchema>;

/** Query: cursor opaque optional, limit ∈ [1,50] optional. Validate tại biên (ZodValidationPipe). */
export const memberContributionsQuerySchema = z.object({
  cursor: z.string().max(MEMBER_CONTRIBUTIONS_MAX_QUERY_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(MEMBER_CONTRIBUTIONS_MAX_LIMIT).optional(),
});

export type MemberContributionsQuery = z.infer<typeof memberContributionsQuerySchema>;
